/**
 * SessionJourney
 * V11 Feature 2: Emotional Arc Memory
 * 
 * Tracks the emotional journey of the entire session to create
 * a sense of "completeness" and narrative arc.
 * 
 * - Peak Memory: Records high-intensity moments with chord snapshots
 * - Callback System: Occasionally recalls peak progressions later
 * - Ending Anticipation: Detects "winding down" and biases toward resolution
 */

export interface PeakMoment {
    timestamp: number;
    chordSnapshot: number[];     // MIDI notes of the chord at peak
    intensity: number;           // 0-1 intensity level at peak
    touchCount: number;          // Touch count when peak was recorded
}

export interface JourneyHint {
    shouldRecallPeak: boolean;
    recalledChord: number[] | null;
    isApproachingEnd: boolean;
    suggestedResolution: 'strong' | 'soft' | 'none';
    sessionPhase: 'opening' | 'development' | 'climax' | 'resolution';
}

export class SessionJourney {
    private sessionStartTime: number = 0;
    private peaks: PeakMoment[] = [];
    private readonly MAX_PEAKS = 5;  // Store top 5 peaks

    // Intensity tracking
    private recentIntensities: number[] = [];
    private readonly INTENSITY_WINDOW = 12;

    // Activity tracking for ending detection
    private recentTouchIntervals: number[] = [];
    private lastTouchTime: number = 0;

    // Callback control
    private lastCallbackTime: number = 0;
    private readonly CALLBACK_COOLDOWN_MS = 30000; // 30 seconds between callbacks
    private readonly MIN_SESSION_FOR_CALLBACK_MS = 120000; // 2 minutes before callbacks

    constructor() {
        this.reset();
    }

    reset(): void {
        this.sessionStartTime = Date.now();
        this.peaks = [];
        this.recentIntensities = [];
        this.recentTouchIntervals = [];
        this.lastTouchTime = Date.now();
        this.lastCallbackTime = 0;
    }

    /**
     * Record a touch with its intensity and current chord context.
     * Call this on every touch to build the journey map.
     */
    recordTouch(intensity: number, currentChord: number[], touchCount: number): void {
        const now = Date.now();

        // Track touch intervals for ending detection
        if (this.lastTouchTime > 0) {
            const interval = now - this.lastTouchTime;
            this.recentTouchIntervals.push(interval);
            if (this.recentTouchIntervals.length > 8) {
                this.recentTouchIntervals.shift();
            }
        }
        this.lastTouchTime = now;

        // Track intensity history
        this.recentIntensities.push(intensity);
        if (this.recentIntensities.length > this.INTENSITY_WINDOW) {
            this.recentIntensities.shift();
        }

        // Detect peak moments (above-average intensity + substantial chord)
        if (this.isPeakMoment(intensity) && currentChord.length >= 3) {
            this.recordPeak({
                timestamp: now,
                chordSnapshot: [...currentChord],
                intensity,
                touchCount
            });
        }
    }

    /**
     * Get journey hints for the current moment.
     * Call this when generating harmony to potentially inject callbacks.
     */
    getHint(currentTouchCount: number): JourneyHint {
        const now = Date.now();
        const sessionDuration = now - this.sessionStartTime;
        const sessionPhase = this.getSessionPhase(sessionDuration, currentTouchCount);
        const isApproachingEnd = this.detectApproachingEnd();

        // Callback logic
        let shouldRecallPeak = false;
        let recalledChord: number[] | null = null;

        if (this.canTriggerCallback(now, sessionDuration)) {
            const peakToRecall = this.selectPeakForCallback();
            if (peakToRecall && Math.random() < 0.3) { // 30% chance when eligible
                shouldRecallPeak = true;
                recalledChord = peakToRecall.chordSnapshot;
                this.lastCallbackTime = now;
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[SessionJourney] Peak callback triggered - recalling chord from touch #${peakToRecall.touchCount}`);
                }
            }
        }

        // Suggest resolution strength based on phase
        let suggestedResolution: 'strong' | 'soft' | 'none' = 'none';
        if (isApproachingEnd) {
            suggestedResolution = 'strong';
        } else if (sessionPhase === 'resolution') {
            suggestedResolution = 'soft';
        }

        return {
            shouldRecallPeak,
            recalledChord,
            isApproachingEnd,
            suggestedResolution,
            sessionPhase
        };
    }

    /**
     * Determine if current moment qualifies as a "peak".
     */
    private isPeakMoment(intensity: number): boolean {
        if (this.recentIntensities.length < 4) return false;

        const avg = this.recentIntensities.reduce((a, b) => a + b, 0) / this.recentIntensities.length;

        // Peak = intensity significantly above recent average
        return intensity > avg * 1.3 && intensity > 0.6;
    }

    /**
     * Record a peak moment, maintaining top N peaks.
     */
    private recordPeak(peak: PeakMoment): void {
        this.peaks.push(peak);

        // Keep only top peaks by intensity
        if (this.peaks.length > this.MAX_PEAKS) {
            this.peaks.sort((a, b) => b.intensity - a.intensity);
            this.peaks = this.peaks.slice(0, this.MAX_PEAKS);
        }

        if (process.env.NODE_ENV === 'development') {
            console.log(`[SessionJourney] Peak recorded (intensity: ${peak.intensity.toFixed(2)})`);
        }
    }

    /**
     * Check if we're eligible to trigger a callback.
     */
    private canTriggerCallback(now: number, sessionDuration: number): boolean {
        // Must have 2+ minutes of session
        if (sessionDuration < this.MIN_SESSION_FOR_CALLBACK_MS) return false;

        // Must have at least one peak recorded
        if (this.peaks.length === 0) return false;

        // Cooldown between callbacks
        if (now - this.lastCallbackTime < this.CALLBACK_COOLDOWN_MS) return false;

        return true;
    }

    /**
     * Select which peak to callback (prefers older peaks for nostalgia effect).
     */
    private selectPeakForCallback(): PeakMoment | null {
        if (this.peaks.length === 0) return null;

        // Bias toward earlier peaks (the "coming home" feeling)
        const sortedByTime = [...this.peaks].sort((a, b) => a.timestamp - b.timestamp);

        // 60% chance first peak, 25% second, 15% random
        const roll = Math.random();
        if (roll < 0.6 && sortedByTime.length >= 1) {
            return sortedByTime[0];
        } else if (roll < 0.85 && sortedByTime.length >= 2) {
            return sortedByTime[1];
        }
        return sortedByTime[Math.floor(Math.random() * sortedByTime.length)];
    }

    /**
     * Detect if the session is "winding down" (approaching end).
     */
    private detectApproachingEnd(): boolean {
        if (this.recentTouchIntervals.length < 4) return false;

        // Calculate average of first half vs second half
        const mid = Math.floor(this.recentTouchIntervals.length / 2);
        const firstHalf = this.recentTouchIntervals.slice(0, mid);
        const secondHalf = this.recentTouchIntervals.slice(mid);

        const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
        const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

        // If touches are slowing down significantly, approaching end
        return secondAvg > firstAvg * 1.5;
    }

    /**
     * Determine the current phase of the session.
     */
    private getSessionPhase(
        durationMs: number,
        touchCount: number
    ): 'opening' | 'development' | 'climax' | 'resolution' {
        // Phase based on time (rough estimates)
        const minutes = durationMs / 60000;

        if (minutes < 0.5 || touchCount < 16) {
            return 'opening';
        } else if (minutes < 2) {
            return 'development';
        } else if (this.isInClimaxZone()) {
            return 'climax';
        } else {
            return 'resolution';
        }
    }

    /**
     * Check if we're in a high-intensity zone (potential climax).
     */
    private isInClimaxZone(): boolean {
        if (this.recentIntensities.length < 4) return false;

        const recentAvg = this.recentIntensities
            .slice(-4)
            .reduce((a, b) => a + b, 0) / 4;

        return recentAvg > 0.75;
    }

    /**
     * Get session duration in milliseconds.
     */
    getSessionDuration(): number {
        return Date.now() - this.sessionStartTime;
    }
}
